/**
 * EditingPanel.tsx - Media Editing Control Panel Component
 *
 * This component provides the main control panel for media editing with
 * tool selection, settings, and editing options organized in a sidebar.
 *
 * Key Features:
 * - Tool selection and switching interface
 * - Editing settings and parameter controls
 * - Real-time adjustment sliders and inputs
 * - Preset filters and effect applications
 * - Undo/redo functionality with history
 * - Save and export options with quality settings
 *
 * Editing Tools:
 * - Crop Tool: Image cropping and aspect ratio control
 * - Adjust: Brightness, contrast, saturation, hue
 * - Filters: Preset effects and color adjustments
 * - Resize: Dimension and resolution changes
 * - Rotate: Image rotation and flip operations
 *
 * Control Types:
 * - Sliders: Continuous value adjustments
 * - Toggle Switches: On/off settings
 * - Dropdown Menus: Preset selections
 * - Color Pickers: Color-based adjustments
 * - Numeric Inputs: Precise value entry
 *
 * Props:
 * - selectedTool: string - Currently active editing tool
 * - onToolChange: (tool: string) => void - Tool selection handler
 * - editingSettings: EditingSettings - Current editing parameters
 * - onSettingsChange: (settings: EditingSettings) => void - Settings change handler
 *
 * Dependencies: Editing tools, slider components, MobX store
 */

import React, { useState } from "react";
import styled from "styled-components";
import {
  Crop,
  ZoomIn,
  ZoomOut,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Palette,
} from "lucide-react";
import { MediaModel } from "../../models/GalleryModels";
import { fundraiserStore } from "../../stores";

export type EditingTool =
  | "details"
  | "crop"
  | "zoom"
  | "edit"
  | "filters"
  | "rotate"
  | "flip"
  | "ai";

interface EditingPanelProps {
  activeTool: EditingTool;
  mediaItem: MediaModel | null;
  adjust?: {
    brightness: number;
    contrast: number;
    saturation: number;
    blur: number;
  };
  onAdjustChange?: (adjust: any) => void;
  onUpdateMedia?: (updates: Partial<MediaModel>) => void;
  onZoomChange?: (zoom: number) => void;
  zoom?: number;
  onApplyCropPreset?: (preset: string) => void;
  selectedCropPreset?: string;
  onApplyCrop?: () => void;
  onCancelCrop?: () => void;
  onFlipH?: () => void;
  onFlipV?: () => void;
  onRotate?: () => void;
  onRotateChange?: (rotation: number) => void;
  rotation?: number;
  isProcessing?: boolean;
  hasValidApiKey?: boolean;
  flipH?: boolean;
  flipV?: boolean;
  onFlipReset?: () => void;
  onAIEditImage?: (prompt: string) => Promise<void>;
  aiPrompt?: string;
  onAiPromptChange?: (prompt: string) => void;
  cropData?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  cropConstrainedZoom?: boolean;
  cropModified?: boolean;
  onCropApply?: () => void;
  onCropReset?: () => void;
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliderLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const SliderValue = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: auto;
`;

const Slider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #132837;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #132837;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-webkit-slider-track {
    background: #132837;
    height: 4px;
    border-radius: 2px;
  }

  &::-moz-range-track {
    background: #132837;
    height: 4px;
    border-radius: 2px;
  }
`;

const ButtonsSection = styled.div`
  margin-top: 20px;
`;

const PresetButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const PresetButton = styled.button`
  padding: 8px 12px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px; /* Slightly larger for better readability */
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap; /* Prevent text wrapping */
  text-align: center; /* Center button text */
  width: 100%; /* Fill entire grid cell */
  display: flex; /* Use flexbox for perfect centering */
  align-items: center; /* Vertical centering */
  justify-content: center; /* Horizontal centering */
  min-height: 40px; /* Consistent button height */
  box-sizing: border-box; /* Include padding/border in width calculation */

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const ResetButton = styled.button<{ centered?: boolean }>`
  padding: 10px 20px;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: block;
  margin: ${(props) => (props.centered ? "0 auto" : "0")};

  &:hover {
    color: #dc2626;
    text-decoration: underline;
  }

  &:active {
    color: #b91c1c;
  }
`;

const RotateSliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const RotateSliderLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const RotateSliderValue = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: auto;
`;

const RotateSlider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #132837;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #132837;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const AIPromptContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OldResetButton = styled.button`
  padding: 10px 20px;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    color: #dc2626;
    text-decoration: underline;
  }

  &:active {
    color: #b91c1c;
  }
`;

const AISection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: -28px;
`;

const AIPromptInput = styled.textarea`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 18px;
  resize: vertical;
  min-height: 275px;
  transition: border-color 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 18px;
  }
`;

const AIOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const AIOption = styled.button`
  padding: 10px 16px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const AIDisclaimer = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  text-align: center;
  margin-bottom: 30px;
`;

const ZoomControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const ZoomButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ZoomButton = styled.button`
  padding: 10px 16px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const FlipSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FlipButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FlipButton = styled.button<{ active?: boolean }>`
  padding: 16px;
  background: ${(props) => (props.active ? "#3b82f6" : "#f3f4f6")};
  color: ${(props) => (props.active ? "#ffffff" : "#374151")};
  border: 1px solid ${(props) => (props.active ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.active ? "#2563eb" : "#e5e7eb")};
    border-color: ${(props) => (props.active ? "#2563eb" : "#9ca3af")};
  }
`;

const FlipButtonLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const CropPresetsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CropPresetButton = styled.button<{ active?: boolean }>`
  padding: 12px 16px;
  background: ${(props) => (props.active ? "#3b82f6" : "#f3f4f6")};
  color: ${(props) => (props.active ? "#ffffff" : "#374151")};
  border: 1px solid ${(props) => (props.active ? "#3b82f6" : "#d1d5db")};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.active ? "#2563eb" : "#e5e7eb")};
    border-color: ${(props) => (props.active ? "#2563eb" : "#9ca3af")};
  }
`;

const PresetButtonsGrid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr; /* Equal width distribution for 3 buttons */
  gap: 12px; /* Increased gap for better visual separation */
  margin-bottom: 16px;
  width: 100%; /* Ensure grid takes full container width */
`;

const CropActionLink = styled.button<{ variant?: "reset" | "apply" }>`
  background: none;
  border: none;
  color: ${(props) => (props.variant === "reset" ? "#ef4444" : "#3b82f6")};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: block;
  margin: 0 auto;
  padding: 8px 0;

  &:hover {
    color: ${(props) => (props.variant === "reset" ? "#dc2626" : "#2563eb")};
    text-decoration: underline;
  }

  &:active {
    color: ${(props) => (props.variant === "reset" ? "#b91c1c" : "#1d4ed8")};
  }
`;

const CropActionsRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;
export const EditingPanel: React.FC<EditingPanelProps> = ({
  activeTool,
  mediaItem,
  adjust = { brightness: 100, contrast: 100, saturation: 100, blur: 0 },
  onAdjustChange,
  onUpdateMedia,
  onZoomChange,
  zoom = 1,
  onApplyCropPreset,
  selectedCropPreset = "free",
  onApplyCrop,
  onCancelCrop,
  onFlipH,
  onFlipV,
  onRotate,
  onRotateChange,
  rotation = 0,
  isProcessing = false,
  hasValidApiKey = false,
  flipH = false,
  flipV = false,
  onFlipReset,
  aiPrompt = "",
  onAiPromptChange,
  cropData = null,
  cropConstrainedZoom = false,
  cropModified = false,
  onCropApply,
  onCropReset,
  onAIEditImage,
}) => {
  const hasFilterChanges =
    adjust.brightness !== 100 ||
    adjust.contrast !== 100 ||
    adjust.saturation !== 100 ||
    adjust.blur !== 0;
  const hasFlipChanges = flipH || flipV;
  const hasRotateChanges = rotation !== 0;

  // Calculate effective zoom range based on crop constraints
  const getZoomLimits = () => {
    if (cropConstrainedZoom && cropData) {
      // When crop-constrained, zoom range is based on crop area size
      const cropScale = Math.min(cropData.width, cropData.height);
      return {
        min: 1,
        max: Math.max(2, 3 / cropScale), // Allow more zoom for smaller crops
      };
    }
    return { min: 1, max: 3 }; // Default zoom range
  };

  const zoomLimits = getZoomLimits();

  // Update zoom level display for crop-constrained mode
  const getZoomDisplayLevel = () => {
    if (cropConstrainedZoom && cropData) {
      // Show zoom relative to crop area
      return Math.round(zoom * 100);
    }
    return Math.round(zoom * 100);
  };

  // Apply filter presets
  const applyFilterPreset = (preset: string) => {
    switch (preset) {
      case "inviting":
        onAdjustChange?.({
          brightness: 120,
          contrast: 110,
          saturation: 130,
          blur: 0,
        });
        break;
      case "high-impact":
        onAdjustChange?.({
          brightness: 110,
          contrast: 140,
          saturation: 120,
          blur: 0,
        });
        break;
      case "classic-bw":
        onAdjustChange?.({
          brightness: 105,
          contrast: 115,
          saturation: 0,
          blur: 0,
        });
        break;
    }
  };

  // Insert AI prompt suggestions
  const insertAiPrompt = (text: string) => {
    const newPrompt = aiPrompt ? `${aiPrompt} ${text}` : text;
    if (onAiPromptChange) {
      onAiPromptChange(newPrompt);
    }
  };

  // Rotate button handlers with proper range wrapping
  const handleRotateLeft = () => {
    let newRotation = (rotation || 0) - 90;
    // Wrap around if below -180: add 360 to convert to positive range
    if (newRotation < -180) {
      newRotation = newRotation + 360;
    }
    onRotateChange?.(newRotation);
  };

  const handleRotateRight = () => {
    let newRotation = (rotation || 0) + 90;
    // Wrap around if above 180: subtract 360 to convert to negative range
    if (newRotation > 180) {
      newRotation = newRotation - 360;
    }
    onRotateChange?.(newRotation);
  };
  const low = Number(fundraiserStore.LOW_QUALITY_IMAGE_CHARGES.toFixed(1));
  const high = Number(fundraiserStore.HIGH_QUALITY_IMAGE_CHARGES.toFixed(1));

  const renderToolContent = () => {
    switch (activeTool) {
      case "details":
        return (
          <PanelContainer>
            <Section>
              <FormGroup>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  type="text"
                  value={mediaItem?.title || ""}
                  onChange={(e) => onUpdateMedia?.({ title: e.target.value })}
                  placeholder="Enter title"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={mediaItem?.description || ""}
                  onChange={(e) =>
                    onUpdateMedia?.({ description: e.target.value })
                  }
                  placeholder="Enter description"
                />
              </FormGroup>
            </Section>
          </PanelContainer>
        );

      case "crop":
        return (
          <PanelContainer>
            <Section>
              <CropPresetsGrid>
                <CropPresetButton
                  active={selectedCropPreset === "free"}
                  onClick={() => onApplyCropPreset?.("free")}
                >
                  Free
                </CropPresetButton>
                <CropPresetButton
                  active={selectedCropPreset === "16:9"}
                  onClick={() => onApplyCropPreset?.("16:9")}
                >
                  16:9
                </CropPresetButton>
                <CropPresetButton
                  active={selectedCropPreset === "4:3"}
                  onClick={() => onApplyCropPreset?.("4:3")}
                >
                  4:3
                </CropPresetButton>
                <CropPresetButton
                  active={selectedCropPreset === "square"}
                  onClick={() => onApplyCropPreset?.("square")}
                >
                  Square
                </CropPresetButton>
                {/* <CropPresetButton
                  active={selectedCropPreset === "circle"}
                  onClick={() => onApplyCropPreset?.("circle")}
                >
                  Circle
                </CropPresetButton> */}
              </CropPresetsGrid>
              {(cropModified ||
                (selectedCropPreset && selectedCropPreset !== "free")) && (
                <CropActionsRow>
                  <CropActionLink
                    variant="reset"
                    onClick={() => {
                      // Reset to free crop preset and clear crop modified state
                      onApplyCropPreset?.("free");
                      onCropReset?.();
                    }}
                  >
                    Reset
                  </CropActionLink>
                </CropActionsRow>
              )}
            </Section>
          </PanelContainer>
        );

      case "zoom":
        return (
          <PanelContainer>
            <Section>
              <ZoomControls>
                <ZoomButtonGroup>
                  <ZoomButton
                    onClick={() =>
                      onZoomChange?.(Math.max(zoomLimits.min, zoom - 0.1))
                    }
                  >
                    <ZoomOut size={16} />
                    Zoom Out
                  </ZoomButton>
                  <ZoomButton
                    onClick={() =>
                      onZoomChange?.(Math.min(zoomLimits.max, zoom + 0.1))
                    }
                  >
                    <ZoomIn size={16} />
                    Zoom In
                  </ZoomButton>
                </ZoomButtonGroup>
                <div>
                  <Label>
                    Zoom Level: {getZoomDisplayLevel()}%
                    {cropConstrainedZoom ? " (crop area)" : ""}
                  </Label>
                  <Slider
                    type="range"
                    min={zoomLimits.min.toString()}
                    max={zoomLimits.max.toString()}
                    step="0.1"
                    value={zoom}
                    onChange={(e) => onZoomChange?.(parseFloat(e.target.value))}
                  />
                </div>
                {zoom !== 1 && (
                  <ResetButton
                    centered={true}
                    onClick={() => onZoomChange?.(1)}
                  >
                    Reset
                  </ResetButton>
                )}
                {cropConstrainedZoom && cropData && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontStyle: "italic",
                      textAlign: "center",
                      marginTop: "12px",
                      padding: "8px",
                      background: "#f8fafc",
                      borderRadius: "6px",
                    }}
                  >
                    Zoom is constrained to crop area. Pan to view different
                    parts of the cropped region.
                  </div>
                )}
              </ZoomControls>
            </Section>
          </PanelContainer>
        );

      case "ai":
        return (
          <PanelContainer>
            <Section
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <AISection>
                <AIPromptInput
                  placeholder="Describe how you want to edit this image..."
                  value={aiPrompt}
                  onChange={(e) => onAiPromptChange?.(e.target.value)}
                />
                {!isProcessing && (
                  <AIOptionsGrid>
                    <AIOption
                      onClick={() => insertAiPrompt("Remove background")}
                    >
                      Remove background
                    </AIOption>
                    <AIOption
                      onClick={() => insertAiPrompt("Upscale resolution")}
                    >
                      Upscale resolution
                    </AIOption>
                    <AIOption
                      onClick={() => insertAiPrompt("Improve lighting")}
                    >
                      Improve lighting
                    </AIOption>
                    <AIOption onClick={() => insertAiPrompt("Enhance details")}>
                      Enhance details
                    </AIOption>
                  </AIOptionsGrid>
                )}
              </AISection>
              <div style={{ flex: 1 }} />
              <AIDisclaimer>
                {fundraiserStore.isAICreditsDataNull
                  ? "You do not have enough AI credits to use this feature."
                  : `AI image editing costs ${fundraiserStore.LOW_QUALITY_IMAGE_CHARGES}-${fundraiserStore.HIGH_QUALITY_IMAGE_CHARGES} credits per request.`}
              </AIDisclaimer>
            </Section>
          </PanelContainer>
        );

      case "filters":
        return (
          <PanelContainer>
            <Section>
              <SliderGroup>
                <SliderContainer>
                  <SliderLabel>
                    Brightness
                    <SliderValue>{adjust.brightness}%</SliderValue>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="200"
                    value={adjust.brightness}
                    onChange={(e) =>
                      onAdjustChange?.({
                        ...adjust,
                        brightness: parseInt(e.target.value),
                      })
                    }
                  />
                </SliderContainer>

                <SliderContainer>
                  <SliderLabel>
                    Contrast
                    <SliderValue>{adjust.contrast}%</SliderValue>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="200"
                    value={adjust.contrast}
                    onChange={(e) =>
                      onAdjustChange?.({
                        ...adjust,
                        contrast: parseInt(e.target.value),
                      })
                    }
                  />
                </SliderContainer>

                <SliderContainer>
                  <SliderLabel>
                    Saturation
                    <SliderValue>{adjust.saturation}%</SliderValue>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="200"
                    value={adjust.saturation}
                    onChange={(e) =>
                      onAdjustChange?.({
                        ...adjust,
                        saturation: parseInt(e.target.value),
                      })
                    }
                  />
                </SliderContainer>

                <SliderContainer>
                  <SliderLabel>
                    Blur
                    <SliderValue>{adjust.blur}px</SliderValue>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="20"
                    value={adjust.blur}
                    onChange={(e) =>
                      onAdjustChange?.({
                        ...adjust,
                        blur: parseInt(e.target.value),
                      })
                    }
                  />
                </SliderContainer>
              </SliderGroup>

              <ButtonsSection>
                <PresetButtonsGrid>
                  <PresetButton
                    onClick={() =>
                      onAdjustChange?.({
                        brightness: 120,
                        contrast: 110,
                        saturation: 130,
                        blur: 0,
                      })
                    }
                  >
                    Inviting
                  </PresetButton>
                  <PresetButton
                    onClick={() =>
                      onAdjustChange?.({
                        brightness: 110,
                        contrast: 140,
                        saturation: 120,
                        blur: 0,
                      })
                    }
                  >
                    High Impact
                  </PresetButton>
                  <PresetButton
                    onClick={() =>
                      onAdjustChange?.({
                        brightness: 105,
                        contrast: 115,
                        saturation: 0,
                        blur: 0,
                      })
                    }
                  >
                    Classic B&W
                  </PresetButton>
                </PresetButtonsGrid>
                {hasFilterChanges && (
                  <ResetButton
                    centered={true}
                    onClick={() =>
                      onAdjustChange?.({
                        brightness: 100,
                        contrast: 100,
                        saturation: 100,
                        blur: 0,
                      })
                    }
                  >
                    Reset
                  </ResetButton>
                )}
              </ButtonsSection>
            </Section>
          </PanelContainer>
        );

      case "flip":
        return (
          <PanelContainer>
            <Section>
              <FlipSection>
                <FlipButtonsGrid>
                  <FlipButton active={flipH} onClick={onFlipH}>
                    <FlipHorizontal size={24} />
                    <FlipButtonLabel>Horizontal</FlipButtonLabel>
                  </FlipButton>

                  <FlipButton active={flipV} onClick={onFlipV}>
                    <FlipVertical size={24} />
                    <FlipButtonLabel>Vertical</FlipButtonLabel>
                  </FlipButton>
                </FlipButtonsGrid>

                {(flipH || flipV) && (
                  <ResetButton centered={true} onClick={onFlipReset}>
                    Reset
                  </ResetButton>
                )}
              </FlipSection>
            </Section>
          </PanelContainer>
        );

      case "rotate":
        return (
          <PanelContainer>
            <Section>
              <FlipButtonsGrid>
                <FlipButton onClick={handleRotateRight}>
                  <RotateCw size={24} />
                  <FlipButtonLabel>90° Right</FlipButtonLabel>
                </FlipButton>

                <FlipButton onClick={handleRotateLeft}>
                  <RotateCw size={24} style={{ transform: "scaleX(-1)" }} />
                  <FlipButtonLabel>90° Left</FlipButtonLabel>
                </FlipButton>
              </FlipButtonsGrid>

              <RotateSliderContainer>
                <RotateSliderLabel>
                  Rotation
                  <RotateSliderValue>{rotation}°</RotateSliderValue>
                </RotateSliderLabel>
                <RotateSlider
                  type="range"
                  min="-180"
                  max="180"
                  value={rotation}
                  onChange={(e) => onRotateChange?.(parseInt(e.target.value))}
                />
              </RotateSliderContainer>

              {hasRotateChanges && (
                <ResetButton
                  centered={true}
                  onClick={() => onRotateChange?.(0)}
                >
                  Reset
                </ResetButton>
              )}
            </Section>
          </PanelContainer>
        );

      default:
        return (
          <PanelContainer>
            <Section>
              <p>Select a tool to begin editing</p>
            </Section>
          </PanelContainer>
        );
    }
  };

  return renderToolContent();
};
